import {Button, Col, Form, Row} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {RELATIONSHIP_OPTIONS} from '../../../helpers/catalogs'
import {INIT_FORM_MEMBERS} from '../../../helpers/policyFormDefaults'

type TMember = typeof INIT_FORM_MEMBERS

interface IProps {
  onPrev: () => void
  onNext: () => void
  members: TMember[]
  onChange: (index: number, field: string, value: string | boolean) => void
  onAdd: () => void
  onRemove: (index: number) => void
}

export const FamilyMembersTabModal: React.FC<IProps> = ({
  onPrev,
  onNext,
  members,
  onChange,
  onAdd,
  onRemove,
}) => {
  const intl = useIntl()
  return (
    <div className='family-members-container'>
      <div className='kt-heading kt-heading--md custom-border-brand mb-5'>
        <FormattedMessage
          id='policies.list.wizard.family.title'
          defaultMessage='Applicants & Dependents'
        />
      </div>

      {members.length === 0 && (
        <div className='text-center text-muted py-4 fst-italic'>
          <FormattedMessage
            id='policies.list.wizard.family.empty'
            defaultMessage='No family members added yet.'
          />
        </div>
      )}

      {members.map((member, index) => (
        <div key={index} className='card mb-3 shadow-sm'>
          <div
            className='card-header d-flex justify-content-between align-items-center'
            style={{backgroundColor: '#f5f5ff'}}
          >
            <div className='d-flex align-items-center gap-2'>
              <i className='bi bi-person-fill text-primary' />
              <span className='fw-semibold'>
                {member.firstName || member.lastName
                  ? `${member.firstName} ${member.lastName}`.trim()
                  : intl.formatMessage(
                      {id: 'policies.list.wizard.family.memberN', defaultMessage: 'Member {n}'},
                      {n: index + 1}
                    )}
              </span>
              {member.dateBirth && (
                <span className='badge bg-light text-dark border ms-2' style={{fontSize: '11px'}}>
                  <FormattedMessage
                    id='policies.list.wizard.family.dobLabel'
                    defaultMessage='DOB:'
                  /> {member.dateBirth}
                </span>
              )}
              {member.gender && (
                <span className='badge bg-light text-dark border' style={{fontSize: '11px'}}>
                  {member.gender}
                </span>
              )}
            </div>
            <Button
              variant='outline-danger'
              size='sm'
              onClick={() => onRemove(index)}
            >
              <i className='bi bi-trash' />
            </Button>
          </div>

          <div className='card-body'>
            <Row className='mb-3'>
              <Col md={6}>
                <Form.Label>
                  <FormattedMessage id='common.field.firstName' defaultMessage='First Name' />
                </Form.Label>
                <Form.Control
                  value={member.firstName}
                  onChange={(e) => onChange(index, 'firstName', e.target.value)}
                  placeholder={intl.formatMessage({
                    id: 'common.placeholder.enterFirstName',
                    defaultMessage: 'Enter first name',
                  })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>
                  <FormattedMessage id='common.field.lastName' defaultMessage='Last Name' />
                </Form.Label>
                <Form.Control
                  value={member.lastName}
                  onChange={(e) => onChange(index, 'lastName', e.target.value)}
                  placeholder={intl.formatMessage({
                    id: 'common.placeholder.enterLastName',
                    defaultMessage: 'Enter last name',
                  })}
                />
              </Col>
            </Row>

            <Row className='mb-3'>
              <Col md={6}>
                <Form.Label>
                  <FormattedMessage
                    id='common.field.dateOfBirth'
                    defaultMessage='Date of Birth'
                  />
                  {member.dateBirth && (
                    <span className='text-success ms-2' style={{fontSize: '11px'}}>
                      <i className='bi bi-check-circle-fill' /> <FormattedMessage
                        id='policies.list.wizard.family.preloaded'
                        defaultMessage='Pre-loaded'
                      />
                    </span>
                  )}
                </Form.Label>
                <Form.Control
                  type='date'
                  value={member.dateBirth}
                  onChange={(e) => onChange(index, 'dateBirth', e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>
                  <FormattedMessage id='common.field.gender' defaultMessage='Gender' />
                  {member.gender && (
                    <span className='text-success ms-2' style={{fontSize: '11px'}}>
                      <i className='bi bi-check-circle-fill' /> <FormattedMessage
                        id='policies.list.wizard.family.preloaded'
                        defaultMessage='Pre-loaded'
                      />
                    </span>
                  )}
                </Form.Label>
                <Form.Select
                  value={member.gender}
                  onChange={(e) => onChange(index, 'gender', e.target.value)}
                >
                  <option value=''>
                    {intl.formatMessage({
                      id: 'common.placeholder.selectOption',
                      defaultMessage: 'Select an option',
                    })}
                  </option>
                  <option value='Male'>
                    {intl.formatMessage({id: 'common.gender.male', defaultMessage: 'Male'})}
                  </option>
                  <option value='Female'>
                    {intl.formatMessage({id: 'common.gender.female', defaultMessage: 'Female'})}
                  </option>
                </Form.Select>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Label>
                  <FormattedMessage
                    id='policies.list.wizard.family.relationship'
                    defaultMessage='Relationship'
                  />
                </Form.Label>
                <Form.Select
                  value={member.relationship}
                  onChange={(e) => onChange(index, 'relationship', e.target.value)}
                >
                  <option value=''>
                    {intl.formatMessage({
                      id: 'common.placeholder.selectOption',
                      defaultMessage: 'Select an option',
                    })}
                  </option>
                  {RELATIONSHIP_OPTIONS.map(({value, label}) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6} className='d-flex align-items-end pb-1'>
                <Form.Check
                  type='switch'
                  id={`applicant-${index}`}
                  label={intl.formatMessage({
                    id: 'policies.list.wizard.family.applicantForCoverage',
                    defaultMessage: 'Applicant for coverage',
                  })}
                  checked={member.applicant}
                  onChange={(e) => onChange(index, 'applicant', e.target.checked)}
                />
              </Col>
            </Row>
          </div>
        </div>
      ))}

      <div className='mt-3 mb-5'>
        <Button variant='success' size='sm' onClick={onAdd}>
          <i className='bi bi-plus' /> <FormattedMessage
            id='policies.list.wizard.family.addMember'
            defaultMessage='Add member'
          />
        </Button>
      </div>

      <div className='btn-prev-next'>
        <Button size='sm' variant='secondary' onClick={onPrev}>
          <FormattedMessage id='policies.list.wizard.previous' defaultMessage='PREVIOUS' />
        </Button>
        <Button size='sm' onClick={onNext}>
          <FormattedMessage id='policies.list.wizard.nextStep' defaultMessage='NEXT STEP' />
        </Button>
      </div>
    </div>
  )
}
